import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PWADownloadIconProps {
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    showTooltip?: boolean;
}

export const PWADownloadIcon: React.FC<PWADownloadIconProps> = ({
    className = '',
    size = 'sm',
    showTooltip = true,
}) => {
    // --- Test environment guard ---
    // Only call usePWAInstall if running in a browser (not in SSR or test)
    const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
    // If not in a browser (e.g., in Vitest/JSDOM test), render nothing
    if (!isBrowser) {
        return null;
    }

    const { t } = useTranslation();
    const {
        isInstallable,
        isInstalled,
        showInstallPrompt,
        triggerInstall,
        showInstallTip,
    } = usePWAInstall();

    // Detect Safari (iOS & macOS) so we can always show instructions
    const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMac = typeof navigator !== 'undefined' && /Macintosh|MacIntel/.test(navigator.userAgent);
    const isSafari = typeof navigator !== 'undefined' && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;
    const isMacSafari = isMac && isSafari;

    const [isInstalling, setIsInstalling] = useState(false);

    // Show icon if:
    //   • App not installed AND
    //   • (User is on Safari → we always want to nudge) OR (our visit-count logic says so)
    const shouldRenderIcon = !isInstalled && (showInstallPrompt || isIOSSafari || isMacSafari);

    if (!shouldRenderIcon) {
        return null;
    }

    const handleInstall = async (e: React.MouseEvent) => {
        // Prevent event bubbling to avoid interfering with snackbar
        e.stopPropagation();

        if (!isInstallable) {
            // Show the snackbar tip
            showInstallTip();
            return;
        }
        setIsInstalling(true);
        try {
            const success = await triggerInstall();
            if (success) {
                console.log('🎉 Installation initiated successfully!');
            }
        } catch (error) {
            console.error('❌ Installation failed:', error);
        } finally {
            setIsInstalling(false);
        }
    };

    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
    };

    const buttonClasses = {
        sm: 'h-8 w-8',
        md: 'h-9 w-9',
        lg: 'h-10 w-10',
    };

    const icon = (
        <Button
            onClick={handleInstall}
            disabled={isInstalling}
            variant="ghost"
            size="sm"
            className={`${buttonClasses[size]} p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors ${className}`}
        >
            {isInstalling ? (
                <div className={`${sizeClasses[size]} border border-blue-600 border-t-transparent rounded-full animate-spin`} />
            ) : (
                <Download className={sizeClasses[size]} />
            )}
        </Button>
    );

    return icon;
};
